/**
 * @file askErrors.ts
 * @brief Ask failures in words a person can act on (web + Mac desktop).
 *
 * @details
 * DES 15-10-2026 (Ask Phase 1): the monthly limit, Ask being off, no
 * connection or a server error, instead of "Could not complete the search".
 * Same wording as the phone (FRONTEND MeetingIntelligence/askErrors.ts).
 * Plain functions of the status, code and quota, so the node tests can load it.
 *
 * 22-10-2026 (W1, BACKEND contract "22-10-2026 BACKEND" section 4):
 * - The allowance is the code `ASK_LIMIT_REACHED`, not any 429 (a future
 *   rate limit is also a 429).
 * - The limit says when it resets, from `quota.periodEnd` (Free and no plan
 *   reset on the 1st, UTC; a plan on its billing date).
 * - The count line shows only while the limit is enforced (Free always is),
 *   never "(not enforced yet)".
 * - Free and no-plan users get a way out (web + Mac only; the phone has no
 *   link, Apple rule): personal "Pro has unlimited Ask AI", a company admin
 *   "Business has unlimited Ask AI", a member "Ask your company admin…".
 *
 * B20 / W17 (24-10-2026): one-time Free (`quota.resetMode` "one_time"): the
 * questions are a total, once. "7 of 10 questions left · ends 23 Nov" (or no
 * date when it never ends), and used up says so, never a reset day.
 *
 * @author DES
 * @date 15-10-2026
 */

export const ASK_LIMIT_REACHED = "ASK_LIMIT_REACHED";

/** The allowance as the server sends it (`quota` on search and quota reads). */
export type AskQuota = {
  limit?: number | null;
  used?: number | null;
  remaining?: number | null;
  planCode?: string | null;
  periodEnd?: string | null;
  enforced?: boolean | null;
  /** B20: Free "monthly" | "one_time". Absent on an older backend = monthly. */
  resetMode?: string | null;
  /** B20: null in one-time mode. */
  resetsAt?: string | null;
  /** B20: one-time Free ends then (null = never). */
  expiresAt?: string | null;
};

const isOnce = (q: AskQuota | null | undefined) => q?.resetMode === "one_time";

/** "23 Nov" (UTC). */
function shortDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

const ended = (q: AskQuota | null | undefined) =>
  !!q?.expiresAt && Number.isFinite(Date.parse(q.expiresAt)) && Date.parse(q.expiresAt) <= Date.now();

const OLD_LIMIT_TEXT =
  "You have used all your questions for this month. Your allowance resets at the start of your next billing period.";

/**
 * The reset day, "1 November". `periodEnd` is the end of the period
 * (exclusive): the first moment of the new one, so it is the reset day. Read
 * in UTC, as the server counts the month.
 */
export function askResetDate(periodEnd: string | null | undefined): string | null {
  if (!periodEnd) return null;
  const t = Date.parse(periodEnd);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: "UTC" });
}

const isFree = (q: AskQuota | null | undefined) => (q?.planCode ?? "").toLowerCase() === "free";

/** What the limit says once it is reached. */
export function askLimitText(quota: AskQuota | null | undefined): string {
  if (isOnce(quota)) {
    if (ended(quota)) return "Your free questions have ended.";
    return typeof quota?.limit === "number"
      ? `You've used your ${quota.limit} free questions.`
      : "You've used your free questions.";
  }
  const date = askResetDate(quota?.periodEnd);
  if (!date) return OLD_LIMIT_TEXT;
  if (isFree(quota)) {
    return typeof quota?.limit === "number"
      ? `Your ${quota.limit} free questions reset on ${date}.`
      : `Your free questions reset on ${date}.`;
  }
  return `You have used all your questions for this period. They reset on ${date}.`;
}

/**
 * @param status HTTP status of the failed Ask; 0 or undefined = no response
 * @param message the server's own message, used for other 4xx errors
 * @param code the server's error code (`ASK_LIMIT_REACHED` = the allowance)
 * @param quota the `quota` sent with the limit
 */
export function askErrorText(
  status: number | undefined,
  message?: string,
  code?: string,
  quota?: AskQuota | null,
): string {
  if (code === ASK_LIMIT_REACHED) return askLimitText(quota);
  if (status === 429) return "Too many questions at once. Wait a moment and try again.";
  if (status === 503) return "Ask AI is switched off right now. Try again later.";
  if (!status) return "Could not reach VERBLIZR. Check your connection and try again.";
  if (status >= 500) return "Something went wrong on our side. Try again.";
  return message || "Could not complete the search. Please try again.";
}

/** Whether the count is shown and counts: a number, and enforced. An old
 *  server without `enforced` counts as enforced. */
function counts(quota: AskQuota | null | undefined): quota is AskQuota & { remaining: number } {
  return !!quota && typeof quota.remaining === "number" && quota.enforced !== false;
}

/** "7 of 10 questions left · resets 1 November", or null (unlimited, or not
 *  enforced: nobody is told about a limit that does not hold). */
export function askQuotaLine(quota: AskQuota | null | undefined): string | null {
  if (!counts(quota)) return null;
  if (isOnce(quota)) {
    const end = shortDay(quota.expiresAt);
    const left =
      typeof quota.limit === "number"
        ? `${quota.remaining} of ${quota.limit} questions left`
        : `${quota.remaining} questions left`;
    return end ? `${left} · ${ended(quota) ? "ended" : "ends"} ${end}` : left;
  }
  const date = askResetDate(quota.periodEnd);
  const left =
    typeof quota.limit === "number"
      ? `${quota.remaining} of ${quota.limit} questions left`
      : `${quota.remaining} questions left`;
  return date ? `${left} · resets ${date}` : left;
}

/** No questions left: the Ask box is off instead of failing on send. */
export function askBlocked(quota: AskQuota | null | undefined): boolean {
  return counts(quota) && quota.remaining <= 0;
}

/**
 * The way out under the count, while the limit counts and there is no paid
 * plan (Free / no plan). Web + Mac only; the phone shows no link (Apple).
 * - personal: "Pro has unlimited Ask AI" → Plans
 * - company admin: "Business has unlimited Ask AI" → company Billing (DES 22-10-2026)
 * - company member: "Ask your company admin for more questions", no link
 */
export function askUpgradeLine(
  quota: AskQuota | null | undefined,
  audience: "personal" | "company_admin" | "company_member",
): { text: string; href: string | null } | null {
  if (!counts(quota)) return null;
  const plan = (quota.planCode ?? "").toLowerCase();
  if (!(plan === "free" || plan === "basic" || plan === "none" || plan === "")) return null;
  if (audience === "company_admin") return { text: "Business has unlimited Ask AI", href: "/company/billing" };
  if (audience === "company_member") return { text: "Ask your company admin for more questions", href: null };
  return { text: "Pro has unlimited Ask AI", href: "/app/billing/plan" };
}
